/**
 * AudioService Adapter for Effects Pipeline
 *
 * Bridges the effects system's SoundPlayer interface to the AudioService.
 * Handles abstract sound ID mapping to actual sound files.
 *
 * @see docs/systems/SOUND_EVENTS.md - Sound system documentation
 * @see tasks/active/T14_SOUND_WIRING_PLAN.md - Implementation plan
 */

import type { SoundPlayer } from './SoundEffect';
import { audioService } from '@/services/AudioService';
import { SOUND_MAPPINGS, getRandomSound } from '@/config/sounds';
import type { SoundEffect as SoundEffectType } from '@/types/ui';
import { log } from '@/utils/log';

// =============================================================================
// AUDIO SERVICE ADAPTER
// =============================================================================

/**
 * Adapter that implements SoundPlayer interface for the effects pipeline.
 *
 * Converts abstract event sound IDs (e.g., 'chip_bet') to actual sound files
 * (e.g., 'chip1', 'chip2', etc.) and plays them via AudioService.
 */
export class AudioServiceAdapter implements SoundPlayer {
  /**
   * Play a sound by its event ID or direct sound ID.
   *
   * @param soundId - Abstract event sound ID (e.g., 'chip_bet') or direct sound ID (e.g., 'chip1')
   * @param options - Optional playback options (volume, rate, pan, loop)
   */
  async play(
    soundId: string,
    options?: {
      volume?: number | undefined;
      rate?: number | undefined;
      pan?: number | undefined;
      loop?: boolean | undefined;
    }
  ): Promise<void> {
    // Check if this is a mapped abstract sound ID
    const mappedSounds = SOUND_MAPPINGS[soundId];

    if (mappedSounds && mappedSounds.length > 0) {
      // Get random variant from mapped sounds
      const actualSound = getRandomSound(soundId);
      if (actualSound) {
        log.audio.debug`[SoundAdapter] Playing mapped: ${soundId} → ${actualSound}`;
        this.playDirect(actualSound, options);
      }
    } else {
      // Try direct play (might be a direct sound ID like 'deal1')
      log.audio.debug`[SoundAdapter] Playing direct: ${soundId}`;
      this.playDirect(soundId as SoundEffectType, options);
    }
  }

  /**
   * Stop a playing sound.
   *
   * @param soundId - Sound ID to stop
   */
  stop(soundId: string): void {
    audioService.stop(soundId);
  }

  /**
   * Play a sound directly via AudioService.
   */
  private playDirect(
    soundId: SoundEffectType,
    options?: {
      volume?: number | undefined;
      rate?: number | undefined;
      pan?: number | undefined;
      loop?: boolean | undefined;
    }
  ): void {
    // AudioService.play doesn't support all options yet,
    // but we pass category and let AudioService handle volume
    const category = options?.loop ? 'ambient' : 'sfx';

    // Note: AudioService currently doesn't support rate/pan options
    // These would need to be added to AudioService if needed
    audioService.play(soundId, category);
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/**
 * Singleton adapter instance for use with setSoundPlayer().
 */
export const audioServiceAdapter = new AudioServiceAdapter();
